import React, { type ReactNode, useEffect, useMemo, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import toNumber from 'lodash/toNumber';
// @ts-expect-error - TS2305 - Module '"react-router-dom"' has no exported member 'ContextRouter'.
import type { ContextRouter } from 'react-router-dom';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadCellType'. Did you mean to use 'import HeadCellType from "@atlaskit/dynamic-table"' instead?
import { DynamicTableStateless, type HeadCellType } from '@atlaskit/dynamic-table';
import Pagination from '@atlaskit/pagination';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import errorImage from '@atlassian/jira-servicedesk-common/src/utils/assets/error.svg';
import type {
	OrderBy,
	Expand,
} from '@atlassian/jira-servicedesk-knowledge-services/src/services/rest/articles/index.tsx';
import type {
	CategoryId,
	GenericError,
	Knowledge,
} from '@atlassian/jira-servicedesk-knowledge-services/src/types.tsx';
import type { PushHistory } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/knowledge-hub-push-provider/index.tsx';
import Link from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/knowledge-hub-spa-link/index.tsx';
import { SpaStatePageReadyOnUpdate } from '@atlassian/jira-spa-state-controller/src/components/page-ready-state/index.tsx';
import type { browseKnowledge } from '../../../state/actions/browse-knowledge/index.tsx';
import MessageCallToAction from '../message-call-to-action/view.tsx';
import { getSortOrder, getSortKey, getSortBy } from './knowledge-table-column/view.tsx';
import messages from './messages.tsx';
import type { KnowledgeTableColumn } from './types.tsx';
import { getCursor, getPageSelectedIndex, getPagesArray } from './utils.tsx';

export type KnowledgeTableProps = {
	isInitiallyLoading: boolean;
	isSearchLoading: boolean;
	error: GenericError | undefined;
	categoryId: CategoryId;
	// go/jfe-eslint
	categoriesSupported: boolean;
	emptyStateComponent: ReactNode;
	noSearchResultsComponent: ReactNode;
	page: number;
	prevCursor: string;
	nextCursor: string;
	searchQuery: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	spaceKeys: Record<any, any>;
	limit: number;
	totalSize: number | undefined;
	knowledgePieces: Knowledge[] | undefined;
	columns: KnowledgeTableColumn[];
	sortBy: OrderBy;
	expansions: Expand[] | undefined;
	history: ContextRouter['history'];
	push: PushHistory;
	onBrowseKnowledge: typeof browseKnowledge;
	onSetPage:
		| ((
				pageNum: number,
				sortBy: OrderBy,
				push: PushHistory,
				history: ContextRouter['history'],
				analyticsEvent?: UIAnalyticsEvent,
				cursor?: string,
				searchQuery?: string,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				spaceKeys?: Record<any, any>,
		  ) => void)
		| undefined;
};

export type ExtendedKnowledgeTableProps = KnowledgeTableProps;

export const KnowledgeTable = ({
	isInitiallyLoading,
	isSearchLoading,
	error,
	sortBy,
	expansions,
	page,
	emptyStateComponent,
	noSearchResultsComponent,
	columns,
	onSetPage,
	history,
	push,
	totalSize = -1,
	knowledgePieces,
	limit: rowsPerPage,
	onBrowseKnowledge,
	categoryId,
	prevCursor,
	nextCursor,
	searchQuery,
	spaceKeys,
}: ExtendedKnowledgeTableProps) => {
	// Ensure page is actually a number.
	// Generally it's sourced from route params which may be a string when implicitly passed down.
	const pageNumber = toNumber(page);
	const intl = useIntl();

	const expansionDeps = (expansions || []).join(';');

	useEffect(
		() => {
			// be careful adding new hook dependencies here!
			// the exhaustive-deps rule is disabled for this useEffect so it won't scream at you
			onBrowseKnowledge &&
				onBrowseKnowledge({
					page: pageNumber,
					limit: rowsPerPage,
					id: categoryId,
					orderBy: sortBy,
					expand: expansions,
				});
		},
		// this eslint rule is disabled as expansionDeps is just a string-concatenation of the expansions array
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[categoryId, sortBy, expansionDeps, onBrowseKnowledge, pageNumber, rowsPerPage],
	);

	const columnKeys = columns.map((c) => c.key).join(';');
	const header = useMemo(
		() => {
			// be careful adding new hook dependencies here!
			// the exhaustive-deps rule is disabled for this useMemo so it won't scream at you
			const headerCells: HeadCellType[] = columns.map((column) => ({
				key: column.key,
				...column.header(intl),
			}));

			return { cells: headerCells };
		},
		// we only need to update the header when the column keys change (which means the columns have changed!)
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[columnKeys, intl],
	);

	// @ts-expect-error - TS2304 - Cannot find name 'RowType'.
	let rows: Array<RowType> = [];
	if (totalSize > 0 && Array.isArray(knowledgePieces)) {
		// each piece of knowledge has its own row
		rows = knowledgePieces.map((knowledge, index) => {
			// Use the zero padded index as part of the key, manually correcting when sorted in descending order
			const indexKey = (
				getSortOrder(sortBy) === 'DESC' ? knowledgePieces.length - 1 - index : index
			)
				.toString()
				.padStart((knowledgePieces.length - 1).toString().length, '0');
			const cells = columns.map((column) => ({
				// we use a row cell key unique to the piece of knowledge
				key: `${indexKey}-${column.key}-${knowledge.id}`,
				content: column.row(intl, index, knowledge, { page }),
			}));

			return {
				// we use a row key unique to the piece of knowledge
				key: `knowledge-${knowledge.id}`,
				cells,
			};
		});
	}

	const paginationi18n = useMemo(
		() => ({
			prev: intl.formatMessage(messages.paginationPrev),
			next: intl.formatMessage(messages.paginationNext),
		}),
		[intl],
	);

	if (error) {
		const errorContent = error.error;
		return (
			<MessageCallToAction
				imageUrl={errorImage}
				header={intl.formatMessage(errorContent.header)}
				description={intl.formatMessage(errorContent.message)}
				primaryAction={
					errorContent.ctaLink && errorContent.ctaText ? (
						<Link to={errorContent.ctaLink || ''}>{intl.formatMessage(errorContent.ctaText)}</Link>
					) : undefined
				}
			/>
		);
	}

	if (isInitiallyLoading || isSearchLoading) {
		return (
			<LoadingContainer>
				<Spinner size="large" delay={300} />
			</LoadingContainer>
		);
	}

	if (totalSize === 0) {
		return (
			<TableContainer>
				<DynamicTableStateless
					head={header}
					sortKey={getSortKey(columns, sortBy)}
					sortOrder={getSortOrder(sortBy)}
					// @ts-expect-error - TS2741 - Property 'label' is missing in type '{ prev: string; next: string; }' but required in type 'I18nShape'.
					paginationi18n={paginationi18n}
				/>
				{searchQuery === '' ? emptyStateComponent : noSearchResultsComponent}
			</TableContainer>
		);
	}

	const selectedIndex = getPageSelectedIndex(nextCursor, prevCursor);
	const maxPages = Math.ceil(totalSize / rowsPerPage);
	const pagesArray = getPagesArray(pageNumber, maxPages, prevCursor, nextCursor);
	const currentPageNumber = pagesArray[selectedIndex];
	const onPageChange = (
		event: MouseEvent<HTMLElement>,
		newPageNumber: number,
		analyticsEvent?: UIAnalyticsEvent,
	) => {
		if (newPageNumber !== currentPageNumber && onSetPage) {
			const cursor = getCursor(currentPageNumber, newPageNumber, prevCursor, nextCursor);
			if (cursor) {
				onSetPage(
					newPageNumber,
					sortBy,
					push,
					history,
					analyticsEvent,
					cursor,
					searchQuery,
					spaceKeys,
				);
			}
		}
	};
	const onPageSort = (sort: unknown) => {
		if (
			typeof sort === 'object' &&
			sort !== null &&
			// @ts-expect-error - TS2339 - Property 'key' does not exist on type 'object'.
			typeof sort.key === 'string' &&
			// @ts-expect-error - TS2339 - Property 'sortOrder' does not exist on type 'object'.
			typeof sort.sortOrder === 'string' &&
			onSetPage
		) {
			onSetPage(
				1,
				// @ts-expect-error - TS2339 - Property 'key' does not exist on type 'object'. | TS2339 - Property 'sortOrder' does not exist on type 'object'.
				getSortBy(columns, sort.key, sort.sortOrder),
				push,
				history,
				undefined,
				'',
				searchQuery,
				spaceKeys,
			);
		}
	};
	/* eslint-disable jira/react/no-style-attribute */
	const articlePagination = () => (
		<Pagination
			pages={pagesArray}
			// @ts-expect-error - TS2322 - Type '(event: MouseEvent<HTMLElement>, newPageNumber: number, analyticsEvent?: UIAnalyticsEvent | undefined) => void' is not assignable to type '(event: SyntheticEvent<Element, Event>, page: number, analyticsEvent?: UIAnalyticsEvent | undefined) => void'.
			onChange={onPageChange}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={{ display: 'flex', justifyContent: 'center' }}
			selectedIndex={selectedIndex}
		/>
	);
	/* eslint-enable jira/react/no-style-attribute */

	const isPaginated = (nextCursor || prevCursor) && totalSize > 0;

	return (
		<>
			<TableContainer>
				<DynamicTableStateless
					head={header}
					rows={rows}
					sortKey={getSortKey(columns, sortBy)}
					sortOrder={getSortOrder(sortBy)}
					onSort={onPageSort}
					isLoading={totalSize < 0 || isSearchLoading}
					// @ts-expect-error - TS2322 - Type '{ prev: string; next: string; }' is not assignable to type 'I18nShape'.
					paginationi18n={paginationi18n}
				/>
				{totalSize >= 0 && <SpaStatePageReadyOnUpdate comparer={[categoryId]} />}
			</TableContainer>
			{isPaginated && articlePagination()}
		</>
	);
};

KnowledgeTable.defaultProps = {
	totalSize: -1,
	knowledgePieces: [],
	expansions: undefined,
	onActionsOpenChange: undefined,
	error: undefined,
	prevCursor: '',
	nextCursor: '',
	searchQuery: '',
	spaceKeys: {},
};

export default KnowledgeTable;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableContainer = styled.div({
	marginTop: token('space.250'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'thead, tbody, tfoot': {
		borderBottom: `2px solid ${token('color.border')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'th, td': {
		wordBreak: 'break-word',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	td: {
		padding: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	th: {
		paddingTop: token('space.100'),
		paddingRight: 0,
		paddingBottom: token('space.100'),
		paddingLeft: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'td:not(:last-child), th:not(:last-child)': {
		paddingRight: token('space.300'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	marginTop: token('space.400'),
});
